/**
 * Dev-only reports browser. Shows ALL reports across users with full
 * filtering, category-grouped sort (Open default), and the bulk-export
 * panel. Lifted out of the dashboard 2026-04-30 to prevent dashboard
 * clutter as report volume grows.
 *
 * Cards tag the reporter name (via userDirectory query) so the dev can
 * scan attribution at a glance. The "Reporter" filter dropdown narrows
 * to one user's reports.
 *
 * Filter tabs:
 *   - Open       (status='open') — default; bulk of attention
 *   - Threads    (hasUnreadForDm) — reporter responded; needs your eyes
 *   - Addressed  (status='addressed') — soft-resolved on dev end
 *   - Resolved   (status='resolved') — reporter confirmed
 *   - All        — every report, grouped by primary category
 *
 * "All" view groups reports by primary category (critical → bug →
 * interface → suggestion → question → feedback) and sorts each bucket
 * by importance descending. Critical sorts above any importance-10.
 */

import React from 'react';
import {
  useAllBugReports,
  useIsDev,
  useUserDirectory,
} from '../hooks/useBugReports.js';
import ReportThread from './ReportThread.jsx';
import ExportPanel from './ExportPanel.jsx';
import {
  TAG_COLORS,
  STATUS_FILL,
  STATUS_BORDER,
  STATUS_LABEL,
  CATEGORY_ORDER,
  primaryCategory,
  normalizeTag,
} from '../tagPalette.js';
import { effectiveData, hasDevEdits, isExcludedFromExport } from '../reportData.js';
import {
  PAGE_WRAP_STYLE,
  PAGE_INNER_STYLE,
  PAGE_TITLE_STYLE,
  SECTION_DIVIDER_STYLE,
  SECTION_HEADING_STYLE,
  SECTION_RULE_STYLE,
} from './MyReportsPage.jsx';

const safeParse = (s) => {
  if (typeof s !== 'string' || !s) return {};
  try { return JSON.parse(s) || {}; } catch { return {}; }
};

const truncateTitle = (title, words = 10) => {
  if (!title) return '(untitled)';
  const arr = title.split(/\s+/);
  if (arr.length <= words) return title;
  return arr.slice(0, words).join(' ') + '…';
};

const renderImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return null;
  if (typeof parsed.importanceRaw === 'number' && typeof parsed.importanceScale === 'number') {
    return `★ ${parsed.importanceRaw}/${parsed.importanceScale}`;
  }
  if (typeof parsed.importance === 'number') {
    return `★ ${parsed.importance}/10`;
  }
  return null;
};

const effectiveImportance = (parsed) => {
  if (Array.isArray(parsed.tags) && parsed.tags.includes('critical')) return 11;
  return typeof parsed.importance === 'number' ? parsed.importance : 0;
};

function ReportCard({ report, onOpen, reporterName }) {
  const parsed = safeParse(report.data);
  // Dev viewer sees the dev-edited title/description/tags if any.
  const eff = effectiveData(parsed, 'dev');
  const tags = (Array.isArray(eff.tags) ? eff.tags : []).map(normalizeTag);
  const unread = report.hasUnreadForDm;
  const edited = hasDevEdits(parsed);
  const excluded = isExcludedFromExport(parsed);
  // Left border = category (scan by color); status communicated by fill
  // + pill. Same UX as MyReportsPage so the two sides feel consistent.
  const cat = primaryCategory(eff.tags);
  const categoryColor = TAG_COLORS[cat] || '#888';
  const statusBorder = STATUS_BORDER[report.status] || '#888';
  const statusFill = STATUS_FILL[report.status] || 'transparent';
  const statusLabel = STATUS_LABEL[report.status] || report.status;
  const importanceLabel = renderImportance(eff);

  const cardBg = report.status === 'open'
    ? 'linear-gradient(180deg, rgba(42,33,55,0.7) 0%, rgba(30,22,36,0.7) 100%)'
    : `linear-gradient(180deg, ${statusFill} 0%, ${statusFill} 100%), linear-gradient(180deg, rgba(42,33,55,0.7) 0%, rgba(30,22,36,0.7) 100%)`;

  return (
    <button
      onClick={() => onOpen(report.reportId)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        gap: 6,
        padding: 14,
        background: cardBg,
        borderRadius: 8,
        border: '1px solid rgba(201,162,39,0.22)',
        borderLeft: `4px solid ${categoryColor}`,
        color: '#e8d5b7',
        cursor: 'pointer',
        boxShadow: unread
          ? `0 0 0 1px ${statusBorder}77, 0 4px 14px rgba(0,0,0,0.45)`
          : '0 2px 10px rgba(0,0,0,0.35)',
        transition: 'transform 120ms ease-out, box-shadow 120ms ease-out, opacity 120ms ease-out',
        minHeight: 130,
        // Excluded cards visually recede so the eye glides past them.
        opacity: excluded ? 0.55 : 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: 8,
          background: `${statusBorder}22`,
          color: statusBorder,
          border: `1px solid ${statusBorder}55`,
          fontSize: '0.62rem',
          fontWeight: 600,
        }}>{statusLabel}</span>
        {unread && (
          <span title="Unread for you" style={{
            width: 8, height: 8, borderRadius: '50%',
            background: statusBorder,
            boxShadow: `0 0 6px ${statusBorder}`,
          }} />
        )}
        {edited && (
          <span title="Dev-edited (overrides reporter's submission for export)" style={{
            padding: '1px 6px', borderRadius: 4, fontSize: '0.55rem',
            background: 'rgba(74,144,217,0.18)',
            border: '1px solid rgba(74,144,217,0.4)',
            color: '#7eb6e8', fontWeight: 600,
          }}>✏️ edited</span>
        )}
        {excluded && (
          <span title="Excluded from bulk export" style={{
            padding: '1px 6px', borderRadius: 4, fontSize: '0.55rem',
            background: 'rgba(184,155,219,0.18)',
            border: '1px solid rgba(184,155,219,0.5)',
            color: '#b89bdb', fontWeight: 600,
          }}>🚫 excluded</span>
        )}
        {importanceLabel && (
          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#c9a227', fontWeight: 600 }}>
            {importanceLabel}
          </span>
        )}
      </div>
      <div style={{
        fontSize: '0.92rem',
        fontWeight: 600,
        color: '#e8d5b7',
        lineHeight: 1.3,
        fontFamily: 'Georgia, "Times New Roman", serif',
      }}>
        {truncateTitle(eff.title, 10)}
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {tags.slice(0, 4).map((t, i) => (
            <span key={`${t}-${i}`} style={{
              padding: '1px 6px',
              borderRadius: 6,
              background: `${TAG_COLORS[t] || '#888'}1a`,
              color: TAG_COLORS[t] || '#888',
              fontSize: '0.58rem',
            }}>{t}</span>
          ))}
        </div>
      )}
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontSize: '0.6rem', color: '#666' }}>
          {new Date(report.lastActivityAt).toLocaleDateString()}
        </span>
        {reporterName && (
          <span
            title={`Reporter: ${reporterName}`}
            style={{
              fontSize: '0.6rem',
              color: '#b0a48a',
              fontStyle: 'italic',
              maxWidth: '60%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            👤 {reporterName}
          </span>
        )}
      </div>
    </button>
  );
}

// A report counts as a "thread" once it has actual back-and-forth
// (commentCount > 0). Questions and feedback are inherently
// conversational, so they show as threads even without a comment yet.
const isThread = (report) => {
  if ((report.commentCount || 0) > 0) return true;
  let parsedTags = [];
  try { parsedTags = JSON.parse(report.data || '{}').tags || []; } catch { /* ignore */ }
  return parsedTags.some(t => t === 'question' || t === 'feedback');
};

const STATUS_FILTERS = [
  { id: 'open',      label: 'Open',         match: r => r.status === 'open' },
  { id: 'threads',   label: '💬 Threads',   match: r => isThread(r) && r.status !== 'resolved', accent: '#4a90d9' },
  { id: 'addressed', label: 'Addressed',    match: r => r.status === 'addressed', accent: '#78dc8c' },
  { id: 'resolved',  label: 'Resolved',     match: r => r.status === 'resolved', accent: '#27c95a' },
  { id: 'all',       label: 'All',          match: () => true },
];

export default function DevReportsPage({ onBack }) {
  const isDev = useIsDev();
  const [openReportId, setOpenReportId] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('open');
  const [tagFilter, setTagFilter] = React.useState(null);
  const [reporterFilter, setReporterFilter] = React.useState('all');

  const all = useAllBugReports(100);
  const items = React.useMemo(() => all.results || [], [all.results]);
  const loading = all.status === 'LoadingFirstPage';
  const canLoadMore = all.status === 'CanLoadMore';

  const directory = useUserDirectory();
  const nameByUserId = React.useMemo(() => {
    const m = new Map();
    if (Array.isArray(directory)) for (const u of directory) m.set(String(u.userId), u.name);
    return m;
  }, [directory]);

  const reporterOptions = React.useMemo(() => {
    const seen = new Map();
    for (const r of items) {
      const id = r.reporterUserId ? String(r.reporterUserId) : '__anon__';
      if (!seen.has(id)) {
        seen.set(id, {
          id,
          name: r.reporterUserId ? (nameByUserId.get(id) || 'Unknown') : 'Anonymous',
          count: 1,
        });
      } else {
        seen.get(id).count++;
      }
    }
    return Array.from(seen.values()).sort((a, b) => b.count - a.count);
  }, [items, nameByUserId]);

  // Apply non-status filters first; status filtering + sorting happens
  // either via the simple per-status path or the category-grouped "All"
  // path below.
  const baseFiltered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = items.slice();
    if (q) {
      out = out.filter(r => {
        const p = safeParse(r.data);
        return (p.title || '').toLowerCase().includes(q)
          || (p.description || '').toLowerCase().includes(q);
      });
    }
    if (tagFilter) {
      out = out.filter(r => {
        const p = safeParse(r.data);
        const tags = (Array.isArray(p.tags) ? p.tags : []).map(normalizeTag);
        return tags.includes(tagFilter);
      });
    }
    if (reporterFilter !== 'all') {
      out = out.filter(r => {
        const id = r.reporterUserId ? String(r.reporterUserId) : '__anon__';
        return id === reporterFilter;
      });
    }
    return out;
  }, [items, search, tagFilter, reporterFilter]);

  // "All" view subsections — grouped by STATUS, in workflow order:
  // Open (needs triage) → Addressed (awaiting reporter) → Resolved
  // (closed). Each card still shows its category color on the left
  // border so the dev can scan both axes simultaneously.
  const groupedForAll = React.useMemo(() => {
    if (statusFilter !== 'all') return null;
    const buckets = new Map();
    buckets.set('open', []);
    buckets.set('addressed', []);
    buckets.set('resolved', []);
    for (const r of baseFiltered) {
      const p = safeParse(r.data);
      const bucket = buckets.get(r.status);
      if (bucket) bucket.push({ report: r, parsed: p });
    }
    for (const arr of buckets.values()) {
      arr.sort((a, b) => {
        const ai = effectiveImportance(a.parsed);
        const bi = effectiveImportance(b.parsed);
        if (ai !== bi) return bi - ai;
        return b.report.lastActivityAt - a.report.lastActivityAt;
      });
    }
    return buckets;
  }, [baseFiltered, statusFilter]);

  const ALL_STATUS_SECTIONS = [
    { id: 'open',      label: '📂 Open',      accent: '#ff6961',
      description: 'Needs triage and a fix.' },
    { id: 'addressed', label: '✨ Addressed',  accent: '#50c8a0',
      description: 'Soft-resolved by you. Awaiting reporter confirmation.' },
    { id: 'resolved',  label: '✅ Resolved',  accent: '#28a046',
      description: 'Confirmed by the reporter.' },
  ];

  const filtered = React.useMemo(() => {
    if (statusFilter === 'all') return [];
    const matchFn = STATUS_FILTERS.find(f => f.id === statusFilter)?.match;
    let out = baseFiltered.slice();
    if (matchFn) out = out.filter(matchFn);
    out.sort((a, b) => {
      if (a.hasUnreadForDm !== b.hasUnreadForDm) return a.hasUnreadForDm ? -1 : 1;
      const ai = effectiveImportance(safeParse(a.data));
      const bi = effectiveImportance(safeParse(b.data));
      if (ai !== bi) return bi - ai;
      return b.lastActivityAt - a.lastActivityAt;
    });
    return out;
  }, [baseFiltered, statusFilter]);

  if (isDev === undefined) {
    return <div style={PAGE_WRAP_STYLE}><div style={PAGE_INNER_STYLE}><div style={{ color: '#888', padding: 20 }}>Loading…</div></div></div>;
  }
  if (!isDev) {
    return (
      <div style={PAGE_WRAP_STYLE}>
        <div style={PAGE_INNER_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  padding: '6px 12px', background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5,
                  color: '#888', cursor: 'pointer', fontSize: '0.78rem',
                }}
              >← Back</button>
            )}
            <h1 style={PAGE_TITLE_STYLE}>🛠️ Dev tools</h1>
          </div>
          <div style={{
            padding: 32, textAlign: 'center', color: '#888',
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(201,162,39,0.2)', borderRadius: 8,
          }}>
            Dev access required.
          </div>
        </div>
      </div>
    );
  }

  if (openReportId) {
    return (
      <div style={PAGE_WRAP_STYLE}>
        <div style={PAGE_INNER_STYLE}>
          <ReportThread
            reportId={openReportId}
            onBack={() => setOpenReportId(null)}
            onOpenReport={(id) => setOpenReportId(id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE_WRAP_STYLE}>
      <div style={PAGE_INNER_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: '6px 12px', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5,
                color: '#888', cursor: 'pointer', fontSize: '0.78rem',
              }}
            >← Back</button>
          )}
          <h1 style={PAGE_TITLE_STYLE}>🛠️ All Reports</h1>
          <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#666' }}>
            {statusFilter === 'all'
              ? `${baseFiltered.length} of ${items.length}`
              : `${filtered.length} of ${items.length}`}
            {' '}report{items.length === 1 ? '' : 's'}
          </span>
        </div>

        <div style={{
          marginBottom: 14, padding: 12,
          background: 'rgba(155,89,182,0.06)',
          border: '1px solid rgba(155,89,182,0.25)',
          borderRadius: 8,
        }}>
          <ExportPanel />
        </div>

        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          padding: '10px 12px', background: 'rgba(0,0,0,0.18)',
          borderRadius: 6, marginBottom: 14,
        }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search title or description…"
            style={{
              flex: '1 1 200px', padding: '5px 10px', fontSize: '0.8rem',
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(201,162,39,0.25)',
              borderRadius: 5, color: '#e8d5b7', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(s => {
              const active = statusFilter === s.id;
              const accent = s.accent || '#c9a227';
              const count = s.id === 'all'
                ? baseFiltered.length
                : baseFiltered.filter(s.match).length;
              return (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(s.id)}
                  style={{
                    padding: '4px 10px', fontSize: '0.7rem',
                    background: active ? `${accent}22` : 'transparent',
                    border: `1px solid ${active ? accent : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: 4,
                    color: active ? accent : '#888',
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <span>{s.label}</span>
                  {count > 0 && (
                    <span style={{
                      fontSize: '0.55rem', padding: '0 5px', borderRadius: 8,
                      background: active ? `${accent}33` : 'rgba(255,255,255,0.05)',
                      color: active ? accent : '#888',
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.62rem', color: '#888', alignSelf: 'center', marginRight: 4 }}>Tag:</span>
            <button
              onClick={() => setTagFilter(null)}
              style={{
                padding: '4px 10px', fontSize: '0.7rem',
                background: !tagFilter ? 'rgba(201,162,39,0.18)' : 'transparent',
                border: `1px solid ${!tagFilter ? '#c9a227' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 4, color: !tagFilter ? '#c9a227' : '#888',
                cursor: 'pointer',
              }}
            >Any</button>
            {CATEGORY_ORDER.map(id => (
              <button
                key={id}
                onClick={() => setTagFilter(tagFilter === id ? null : id)}
                style={{
                  padding: '4px 10px', fontSize: '0.7rem',
                  background: tagFilter === id ? `${TAG_COLORS[id]}22` : 'transparent',
                  border: `1px solid ${tagFilter === id ? TAG_COLORS[id] : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 4,
                  color: tagFilter === id ? TAG_COLORS[id] : '#888',
                  cursor: 'pointer',
                  fontWeight: tagFilter === id ? 600 : 400,
                }}
              >{id}</button>
            ))}
          </div>
          {reporterOptions.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: '0.62rem', color: '#888' }}>Reporter:</span>
              <select
                value={reporterFilter}
                onChange={e => setReporterFilter(e.target.value)}
                style={{
                  padding: '4px 8px', fontSize: '0.72rem',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(201,162,39,0.25)',
                  borderRadius: 4, color: '#e8d5b7',
                }}
              >
                <option value="all">All ({items.length})</option>
                {reporterOptions.map(r => (
                  <option key={r.id} value={r.id}>{r.name} ({r.count})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {loading && <div style={{ color: '#888', padding: 20 }}>Loading reports…</div>}

        {!loading && statusFilter === 'all' && groupedForAll && (
          (() => {
            const totalCount = Array.from(groupedForAll.values()).reduce((acc, arr) => acc + arr.length, 0);
            if (totalCount === 0) {
              return (
                <div style={{
                  padding: 32, textAlign: 'center', color: '#666',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px dashed rgba(201,162,39,0.2)', borderRadius: 8,
                }}>
                  {items.length === 0
                    ? 'No reports across all users yet.'
                    : 'No reports match the current filters.'}
                </div>
              );
            }
            return ALL_STATUS_SECTIONS.map(sec => {
              const arr = groupedForAll.get(sec.id) || [];
              if (arr.length === 0) return null;
              return (
                <div key={sec.id} style={{ marginBottom: 18 }}>
                  <div style={{ ...SECTION_DIVIDER_STYLE, margin: '12px 0 6px' }}>
                    <span style={{ ...SECTION_HEADING_STYLE, color: sec.accent }}>
                      {sec.label} <span style={{ color: '#666', fontWeight: 400 }}>({arr.length})</span>
                    </span>
                    <span style={{ ...SECTION_RULE_STYLE, background: `linear-gradient(90deg, ${sec.accent}66, transparent)` }} />
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: 10, fontStyle: 'italic' }}>
                    {sec.description}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 12,
                  }}>
                    {arr.map(({ report }) => (
                      <ReportCard
                        key={report.reportId}
                        report={report}
                        onOpen={setOpenReportId}
                        reporterName={report.reporterUserId ? nameByUserId.get(String(report.reporterUserId)) : 'Anonymous'}
                      />
                    ))}
                  </div>
                </div>
              );
            });
          })()
        )}

        {!loading && statusFilter !== 'all' && filtered.length === 0 && (
          <div style={{
            padding: 32, textAlign: 'center', color: '#666',
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(201,162,39,0.2)', borderRadius: 8,
          }}>
            {items.length === 0
              ? 'No reports across all users yet.'
              : 'No reports match the current filters.'}
          </div>
        )}

        {!loading && statusFilter !== 'all' && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}>
            {filtered.map(r => (
              <ReportCard
                key={r.reportId}
                report={r}
                onOpen={setOpenReportId}
                reporterName={r.reporterUserId ? nameByUserId.get(String(r.reporterUserId)) : 'Anonymous'}
              />
            ))}
          </div>
        )}

        {canLoadMore && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={() => all.loadMore(50)}
              style={{
                padding: '8px 20px',
                background: 'rgba(74,144,217,0.12)',
                border: '1px solid rgba(74,144,217,0.4)',
                borderRadius: 6, color: '#4a90d9',
                cursor: 'pointer', fontSize: '0.78rem',
              }}
            >
              Load 50 more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
